import React, { useState, useEffect } from 'react';
import { 
  getTaxonomy, 
  updateTaxonomy, 
  addToTaxonomy, 
  initializeTaxonomy,
  checkTaxonomyUsage,
  archiveTaxonomyItem,
  deleteTaxonomyItem
} from '../lib/productTaxonomy';
import {
  getAllDimensionTemplates,
  getDimensionInfo,
  saveTaxonomyDimensions,
  deleteTaxonomyDimensions,
  applyDimensionTemplate
} from '../lib/dimensionConfig';
import TaxonomyDeleteModal from './TaxonomyDeleteModal';

const ProductTaxonomySettings = () => {
  const [taxonomy, setTaxonomy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPath, setSelectedPath] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState({
    label: '',
    type: 'product',
    description: '',
    image: ''
  });
  const [expandedItems, setExpandedItems] = useState({});
  
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [checkingUsage, setCheckingUsage] = useState(false);
  
  // Dimension configuration state
  const [dimensionTemplates, setDimensionTemplates] = useState({});
  const [dimensionInfo, setDimensionInfo] = useState({});
  const [showDimensionModal, setShowDimensionModal] = useState(false);
  const [configuringDimensionsFor, setConfiguringDimensionsFor] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [customDimensions, setCustomDimensions] = useState({});

  useEffect(() => {
    loadTaxonomy();
    loadDimensionData();
  }, []);

  const loadTaxonomy = async () => {
    try {
      setLoading(true);
      const data = await getTaxonomy();
      setTaxonomy(data);
      
      // Load dimension info for all taxonomy paths
      if (data && data.products) {
        await loadDimensionInfoForTree(data.products, []);
      }
    } catch (error) {
      console.error('Error loading taxonomy:', error);
      // Initialize if error loading
      try {
        const initialData = await initializeTaxonomy();
        setTaxonomy(initialData);
      } catch (initError) {
        console.error('Error initializing taxonomy:', initError);
      }
    } finally {
      setLoading(false);
    }
  };
  
  const loadDimensionData = async () => {
    try {
      const templates = await getAllDimensionTemplates();
      setDimensionTemplates(templates);
    } catch (error) {
      console.error('Error loading dimension templates:', error);
    }
  };
  
  const loadDimensionInfoForTree = async (node, path = [], forceRefresh = false) => {
    if (!node || typeof node !== 'object') return;
    
    // Clear all dimension info if force refresh
    if (forceRefresh && path.length === 0) {
      setDimensionInfo({});
    }
    
    for (const [key, value] of Object.entries(node)) {
      if (key === 'metadata') continue;
      
      const currentPath = [...path, key];
      const pathString = `products.${currentPath.join('.')}`;
      
      try {
        const info = await getDimensionInfo(pathString);
        setDimensionInfo(prev => ({
          ...prev,
          [pathString]: info
        }));
      } catch (error) {
        console.error(`Error loading dimension info for ${pathString}:`, error);
      }
      
      // Recursively check children
      if (value && typeof value === 'object') {
        const hasChildren = Object.keys(value).some(k => 
          k !== 'label' && k !== 'description' && k !== 'icon' && 
          k !== 'archived' && k !== 'archivedAt' && typeof value[k] === 'object'
        );
        
        if (hasChildren) {
          await loadDimensionInfoForTree(value, currentPath, false);
        }
      }
    }
  };

  const toggleExpanded = (key) => {
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleAddItem = async () => {
    if (!newItem.label.trim()) {
      alert('Item name is required');
      return;
    }

    // Clean up the item data before saving
    const cleanItem = {
      label: newItem.label.trim(),
      type: newItem.type,
      description: newItem.description.trim() || undefined,
      image: newItem.image.trim() || undefined
    };

    // Remove undefined fields
    Object.keys(cleanItem).forEach(key => {
      if (cleanItem[key] === undefined || cleanItem[key] === '') {
        delete cleanItem[key];
      }
    });

    try {
      // Construct path based on selection (Product → Category → Subcategory → Features → Additionals)
      let path = '';
      if (selectedPath.length === 0) {
        path = 'products';
      } else if (selectedPath.length === 1) {
        path = `products.${selectedPath[0]}`;
      } else if (selectedPath.length === 2) {
        path = `products.${selectedPath[0]}.${selectedPath[1]}`;
      } else if (selectedPath.length === 3) {
        path = `products.${selectedPath[0]}.${selectedPath[1]}.${selectedPath[2]}`;
      } else if (selectedPath.length === 4) {
        path = `products.${selectedPath[0]}.${selectedPath[1]}.${selectedPath[2]}.${selectedPath[3]}`;
      }

      await addToTaxonomy(path, cleanItem);
      await loadTaxonomy();
      
      // Reset form
      setNewItem({
        label: '',
        type: 'product',
        description: '',
        image: ''
      });
      setShowAddForm(false);
    } catch (error) {
      console.error('Error adding item:', error);
    }
  };

  // Handle delete item initiation
  const handleDeleteItem = async (item, currentPath) => {
    // Parse the path to extract the proper keys
    // currentPath might be like ['lighting', 'lampshades', 'readyMade', 'drum'] 
    // or ['lighting', 'subcategories', 'lampshades', 'products', 'readyMade', 'styles', 'drum']
    
    // The currentPath already contains the actual keys without navigation elements
    const actualPath = currentPath;
    
    const [productKey, categoryKey, subcategoryKey, featuresKey, additionalsKey] = actualPath;
    
    const itemToSet = {
      ...item,
      productKey,
      categoryKey,
      subcategoryKey,
      featuresKey,
      additionalsKey,
      type: additionalsKey ? 'additionals' : featuresKey ? 'features' : subcategoryKey ? 'subcategory' : categoryKey ? 'category' : 'product',
      path: actualPath.join(' > ')
    };
    
    console.log('Setting item to delete:', itemToSet);
    setItemToDelete(itemToSet);
    setCheckingUsage(true);
    setShowDeleteModal(true);
    
    try {
      const usage = await checkTaxonomyUsage(productKey, categoryKey, subcategoryKey, featuresKey, additionalsKey);
      setUsageData(usage);
    } catch (error) {
      console.error('Error checking usage:', error);
      setUsageData({ projects: [], totalCount: 0 });
    } finally {
      setCheckingUsage(false);
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async (deleteType) => {
    if (!itemToDelete) return;

    try {
      const { productKey, categoryKey, subcategoryKey, featuresKey, additionalsKey } = itemToDelete;
      
      if (deleteType === 'archive') {
        await archiveTaxonomyItem(productKey, categoryKey, subcategoryKey, featuresKey, additionalsKey);
      } else {
        await deleteTaxonomyItem(productKey, categoryKey, subcategoryKey, featuresKey, additionalsKey);
      }
      
      await loadTaxonomy();
      setShowDeleteModal(false);
      setItemToDelete(null);
      setUsageData(null);
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Error deleting item: ' + error.message);
    }
  };

  // Close delete modal
  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
    setUsageData(null);
  };

  // Dimension configuration functions
  const handleConfigureDimensions = (currentPath) => {
    const pathString = `products.${currentPath.join('.')}`;
    setConfiguringDimensionsFor({
      path: pathString,
      displayPath: currentPath.join(' > ')
    });
    setSelectedTemplate('');
    setCustomDimensions({});
    setShowDimensionModal(true);
  };

  const handleApplyDimensionTemplate = async (templateKey) => {
    if (!configuringDimensionsFor) return;
    
    if (!templateKey) {
      alert('Please select a template to apply');
      return;
    }
    
    try {
      // First, remove any existing configuration to ensure clean state
      const pathString = configuringDimensionsFor.path;
      
      // Clear existing dimension info from state immediately
      setDimensionInfo(prev => {
        const newInfo = { ...prev };
        delete newInfo[pathString];
        return newInfo;
      });
      
      // Apply the new template
      await applyDimensionTemplate(pathString, templateKey);
      
      // Force reload dimension info after a small delay to ensure Firebase is updated
      setTimeout(async () => {
        const info = await getDimensionInfo(pathString);
        setDimensionInfo(prev => ({
          ...prev,
          [pathString]: info
        }));
      }, 200);
      
      setShowDimensionModal(false);
      setSelectedTemplate('');
      alert('Dimension template applied successfully!');
    } catch (error) {
      console.error('Error applying template:', error);
      alert('Error applying template: ' + error.message);
    }
  };

  const handleRemoveDimensions = async (pathString) => {
    if (!window.confirm('Remove dimension configuration for this item?')) {
      return;
    }
    
    try {
      await deleteTaxonomyDimensions(pathString);
      
      // Clear the dimension info for this path and force refresh
      setDimensionInfo(prev => {
        const newInfo = { ...prev };
        delete newInfo[pathString];
        return newInfo;
      });
      
      // Reload dimension info for this specific path to ensure it's properly cleared
      setTimeout(async () => {
        const info = await getDimensionInfo(pathString);
        setDimensionInfo(prev => ({
          ...prev,
          [pathString]: info
        }));
      }, 100);
      
      alert('Dimension configuration removed successfully!');
    } catch (error) {
      console.error('Error removing dimensions:', error);
      alert('Error removing dimensions: ' + error.message);
    }
  };

  const renderTaxonomyTree = (node, path = [], level = 0) => {
    if (!node || typeof node !== 'object') return null;

    return Object.entries(node).map(([key, value]) => {
      if (key === 'metadata') return null;
      
      const currentPath = [...path, key];
      const hasChildren = value && typeof value === 'object' && 
        Object.keys(value).some(k => k !== 'label' && k !== 'description' && k !== 'icon' && k !== 'archived' && k !== 'archivedAt' && typeof value[k] === 'object');
      const isExpanded = expandedItems[currentPath.join('.')];

      return (
        <div key={key} className="relative mb-2">
          {/* Hierarchy Lines */}
          {level > 0 && (
            <>
              {/* Vertical line from parent */}
              <div 
                className="absolute w-0.5 bg-gray-300" 
                style={{ 
                  left: `${(level - 1) * 24 + 12}px`, 
                  top: '-8px', 
                  height: '20px' 
                }}
              />
              {/* Horizontal line to item */}
              <div 
                className="absolute h-0.5 bg-gray-300" 
                style={{ 
                  left: `${(level - 1) * 24 + 12}px`, 
                  top: '12px', 
                  width: '12px' 
                }}
              />
            </>
          )}
          
          <div 
            className={`flex items-center justify-between p-2 rounded cursor-pointer hover:bg-gray-100 ${
              selectedPath.join('.') === currentPath.join('.') ? 'bg-pink-100 border-l-4 border-pink-500' : ''
            }`}
            style={{ marginLeft: `${level * 24}px` }}
            onClick={() => setSelectedPath(currentPath)}
          >
            <div className="flex items-center flex-1">
              {/* Level indicator dot */}
              <div className={`w-2 h-2 rounded-full mr-3 flex-shrink-0 ${
                level === 0 ? 'bg-pink-500' : 
                level === 1 ? 'bg-blue-400' : 
                level === 2 ? 'bg-green-400' : 
                level === 3 ? 'bg-yellow-400' : 'bg-purple-400'
              }`} />
              
              {hasChildren && (
                <button
                  className="mr-2 text-gray-500 hover:text-gray-700 flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpanded(currentPath.join('.'));
                  }}
                >
                  {isExpanded ? '▼' : '▶'}
                </button>
              )}
              
              {/* Level badge */}
              <span className={`text-xs px-2 py-0.5 rounded-full mr-2 flex-shrink-0 ${
                level === 0 ? 'bg-pink-100 text-pink-700' :
                level === 1 ? 'bg-blue-100 text-blue-700' :
                level === 2 ? 'bg-green-100 text-green-700' :
                level === 3 ? 'bg-yellow-100 text-yellow-700' : 'bg-purple-100 text-purple-700'
              }`}>
                L{level + 1}
              </span>
              
              <span className="font-medium">{value.label || key}</span>
              {value.archived && (
                <span className="ml-2 px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                  Archived
                </span>
              )}
              
              {/* Dimension indicators */}
              {(() => {
                const pathString = `products.${currentPath.join('.')}`;
                const dimInfo = dimensionInfo[pathString];
                
                if (dimInfo?.hasConfig) {
                  return (
                    <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-600 rounded flex-shrink-0 flex items-center">
                      📏 {dimInfo.dimensionCount} dims
                      {dimInfo.inheritedFrom && (
                        <span className="ml-1 text-gray-500" title={`Inherited from ${dimInfo.inheritedFrom}`}>↑</span>
                      )}
                    </span>
                  );
                } else {
                  // Check if inherited from parent
                  const parentPaths = [];
                  const parts = currentPath.slice();
                  while (parts.length > 0) {
                    parts.pop();
                    if (parts.length > 0) {
                      parentPaths.push(`products.${parts.join('.')}`);
                    }
                  }
                  
                  const inheritedInfo = parentPaths.find(p => dimensionInfo[p]?.hasConfig);
                  if (inheritedInfo && dimensionInfo[inheritedInfo]) {
                    return (
                      <span className="ml-2 px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded flex-shrink-0 flex items-center">
                        📏 {dimensionInfo[inheritedInfo].dimensionCount} dims ↑
                      </span>
                    );
                  }
                }
                
                return null;
              })()}
              
              {value.description && (
                <span className="ml-2 text-sm text-gray-500">- {value.description}</span>
              )}
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center gap-1">
              {/* Dimension Configuration Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleConfigureDimensions(currentPath);
                }}
                className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors flex-shrink-0"
                title="Configure dimensions"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </button>
              
              {/* Remove Dimensions Button (only show if has direct config) */}
              {(() => {
                const pathString = `products.${currentPath.join('.')}`;
                const dimInfo = dimensionInfo[pathString];
                if (dimInfo?.hasConfig && !dimInfo.inheritedFrom) {
                  return (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveDimensions(pathString);
                      }}
                      className="p-1 text-orange-500 hover:text-orange-700 hover:bg-orange-50 rounded transition-colors flex-shrink-0"
                      title="Remove dimension configuration"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                      </svg>
                    </button>
                  );
                }
                return null;
              })()}
              
              {/* Delete Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteItem(value, currentPath);
                }}
                className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                title="Delete item"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>

          {hasChildren && isExpanded && (
            <div className="relative">
              {/* Vertical continuation line for children */}
              {Object.entries(value)
                .filter(([k, v]) => k !== 'label' && k !== 'description' && k !== 'icon' && k !== 'archived' && k !== 'archivedAt' && typeof v === 'object')
                .length > 0 && (
                <div 
                  className="absolute w-0.5 bg-gray-300" 
                  style={{ 
                    left: `${level * 24 + 12}px`, 
                    top: '0px', 
                    height: '100%' 
                  }}
                />
              )}
              
              {Object.entries(value)
                .filter(([k, v]) => k !== 'label' && k !== 'description' && k !== 'icon' && k !== 'archived' && k !== 'archivedAt' && typeof v === 'object')
                .map(([childKey, childValue]) => (
                  <div key={childKey}>
                    {renderTaxonomyTree({ [childKey]: childValue }, currentPath, level + 1)}
                  </div>
                ))
              }
            </div>
          )}
        </div>
      );
    });
  };

  const renderAddForm = () => (
    <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
      <h3 className="text-lg font-semibold mb-4 text-pink-700">
        Add New Item to: {selectedPath.join(' > ') || 'Root'}
      </h3>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Item Name
          </label>
          <input
            type="text"
            value={newItem.label}
            onChange={(e) => setNewItem(prev => ({ ...prev, label: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
            placeholder="e.g., Accent Chair, Modern Style, etc."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type
          </label>
          <select
            value={newItem.type}
            onChange={(e) => setNewItem(prev => ({ ...prev, type: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
          >
            <option value="product">Product (Level 1)</option>
            <option value="category">Category (Level 2)</option>
            <option value="subcategory">Subcategory (Level 3)</option>
            <option value="features">Features (Level 4)</option>
            <option value="additionals">Additionals (Level 5)</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description (Optional)
          </label>
          <textarea
            value={newItem.description}
            onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
            placeholder="Brief description for customers..."
            rows={2}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Image URL (Optional)
          </label>
          <input
            type="url"
            value={newItem.image}
            onChange={(e) => setNewItem(prev => ({ ...prev, image: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
            placeholder="https://images.unsplash.com/..."
          />
          {newItem.image && (
            <div className="mt-2">
              <img
                src={newItem.image}
                alt="Preview"
                className="w-24 h-24 object-cover rounded-md border"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}
        </div>


        <div className="flex gap-3 pt-4">
          <button
            onClick={handleAddItem}
            className="px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500"
          >
            Add Item
          </button>
          <button
            onClick={() => setShowAddForm(false)}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-600"></div>
        <span className="ml-2 text-gray-600">Loading product taxonomy...</span>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          🦩 Product Taxonomy Management
        </h2>
        <p className="text-gray-600">
          Manage your product categories, styles, and properties for the intelligent quote wizard.
        </p>
      </div>

      {/* Taxonomy Tree - Full Width */}
      <div className="bg-white rounded-lg shadow border border-gray-200 mb-6">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Product Categories</h3>
            <p className="text-sm text-gray-600 mt-1">Click items to expand, select to add sub-items</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={async () => {
                if (taxonomy && taxonomy.products) {
                  setLoading(true);
                  await loadDimensionInfoForTree(taxonomy.products, [], true);
                  setLoading(false);
                }
              }}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
              title="Refresh dimension info"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <button
              onClick={() => setShowAddForm(true)}
              className="px-4 py-2 bg-pink-600 text-white text-sm rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Item
            </button>
          </div>
        </div>
        
        <div className="p-6">
          <div className="taxonomy-tree bg-gray-50 rounded-lg p-6 border-2 border-dashed border-gray-300">
            {taxonomy && taxonomy.products && renderTaxonomyTree(taxonomy.products)}
          </div>
        </div>
      </div>

      {/* Add Form Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full">
            {renderAddForm()}
          </div>
        </div>
      )}

      {/* Instructions and Hierarchy Guide */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">📋 How to Use Product Taxonomy</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-700 mb-3">Hierarchy Levels</h4>
            <div className="space-y-2">
              <div className="flex items-center">
                <div className="w-2 h-2 rounded-full bg-pink-500 mr-3"></div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-pink-100 text-pink-700 mr-2">L1</span>
                <span className="text-sm">Products (e.g., Furniture, Lighting)</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 rounded-full bg-blue-400 mr-3"></div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 mr-2">L2</span>
                <span className="text-sm">Categories (e.g., Seating, Tables)</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 rounded-full bg-green-400 mr-3"></div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 mr-2">L3</span>
                <span className="text-sm">Subcategories (e.g., Armchairs, Sofas)</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 rounded-full bg-yellow-400 mr-3"></div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 mr-2">L4</span>
                <span className="text-sm">Features (e.g., Modern, Traditional)</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 rounded-full bg-purple-400 mr-3"></div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 mr-2">L5</span>
                <span className="text-sm">Additionals (e.g., Colors, Materials)</span>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-medium text-gray-700 mb-3">Instructions</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Click ▶ to expand/collapse items with children</li>
              <li>• Click on any item to select it for adding sub-items</li>
              <li>• Use the "Add Item" button to create new entries</li>
              <li>• Follow the hierarchy: Products → Categories → Subcategories → Features → Additionals</li>
              <li>• Connecting lines show parent-child relationships</li>
              <li>• Color-coded dots and badges indicate the hierarchy level</li>
              <li>• 📏 Icons show dimension configurations (↑ = inherited from parent)</li>
              <li>• Use dimension buttons to configure or remove measurements</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {taxonomy && taxonomy.products && Object.entries(taxonomy.products).map(([key, product]) => (
          <div key={key} className="bg-white rounded-lg shadow border border-gray-200 p-4">
            <h4 className="font-semibold text-gray-800">{product.label}</h4>
            <p className="text-sm text-gray-600 mt-1">
              {product.categories ? Object.keys(product.categories).length : 0} categories
            </p>
          </div>
        ))}
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <TaxonomyDeleteModal
          isOpen={showDeleteModal}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          item={itemToDelete}
          usageData={usageData}
          loading={checkingUsage}
        />
      )}
      
      {/* Dimension Configuration Modal */}
      {showDimensionModal && configuringDimensionsFor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">
              📏 Configure Dimensions
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Path: {configuringDimensionsFor.displayPath}
            </p>
            
            <div className="space-y-4">
              {/* Show current configuration if exists */}
              {(() => {
                const pathString = configuringDimensionsFor.path;
                const dimInfo = dimensionInfo[pathString];
                if (dimInfo?.hasConfig && !dimInfo.inheritedFrom) {
                  // Has direct configuration
                  return (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded mb-4">
                      <p className="text-sm font-medium text-blue-800 mb-1">
                        Current Configuration
                      </p>
                      <p className="text-xs text-blue-700">
                        {dimInfo.name || 'Custom Configuration'} ({dimInfo.dimensionCount} dimensions)
                        {dimInfo.templateKey && !dimensionTemplates[dimInfo.templateKey] && (
                          <span className="ml-2 text-orange-600">(Template: "{dimInfo.templateKey}" - may be user-created)</span>
                        )}
                      </p>
                    </div>
                  );
                } else if (dimInfo?.hasConfig && dimInfo.inheritedFrom) {
                  // Has inherited configuration
                  return (
                    <div className="p-3 bg-gray-50 border border-gray-200 rounded mb-4">
                      <p className="text-sm font-medium text-gray-700 mb-1">
                        Inherited Configuration
                      </p>
                      <p className="text-xs text-gray-600">
                        {dimInfo.name || 'Custom Configuration'} ({dimInfo.dimensionCount} dimensions)
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        From: {dimInfo.inheritedFrom}
                      </p>
                      <p className="text-xs text-blue-600 mt-2">
                        Apply a template here to override the inherited configuration
                      </p>
                    </div>
                  );
                }
                return null;
              })()}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Dimension Template
                </label>
                <select
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Choose a template...</option>
                  {Object.entries(dimensionTemplates).map(([key, template]) => (
                    <option key={key} value={key}>
                      {template.name} ({Object.keys(template.dimensions).length} dimensions)
                    </option>
                  ))}
                </select>
              </div>
              
              {selectedTemplate && (
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Template: {dimensionTemplates[selectedTemplate]?.name}
                  </p>
                  <p className="text-xs text-gray-600">
                    Dimensions: {Object.keys(dimensionTemplates[selectedTemplate]?.dimensions || {}).map(key => 
                      dimensionTemplates[selectedTemplate].dimensions[key].label
                    ).join(', ')}
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => handleApplyDimensionTemplate(selectedTemplate)}
                disabled={!selectedTemplate}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                Apply Template
              </button>
              <button
                onClick={() => setShowDimensionModal(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductTaxonomySettings;